import React, { useState, useReducer, useRef } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import * as mui from '@material-ui/core'
import BootstrapAlert from '../element/BootstrapAlert'

function SelectRegistered (mstAddresses, id, setId) {
  return (
    <>
      <mui.List>
        {mstAddresses.map((address) =>
          <mui.ListItem key={address.id} role={undefined} dense button onClick={(e) => setId(address.id)} className="my-input-root">
            <mui.ListItemIcon>
              <mui.Radio
                edge="start"
                checked={id === address.id}
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': address.id }}
                size="small"
                color="primary"
              />
            </mui.ListItemIcon>
            <mui.ListItemText id={`address-label-${address.id}`}
              primary={`${address.company_name} ${address.department}`}
              secondary={`〒${address.postal_code} ${address.address}`}/>
          </mui.ListItem>
        )}
      </mui.List>
      {(id === -1) && (
        <BootstrapAlert class="danger">
          <div>必ずいずれかを選択してください。</div>
        </BootstrapAlert>
      )}
    </>
  )
}

const stateReducer = (mstAddressesMap, readOnly, sendMode, companyName) => (state, action) => {
  const defaultState = {
    addressId: -1,
    selectRegistered: false,
    inputs: {
      id: null,
      postal_code: '',
      address: '',
      company_name: companyName,
      department: ''
    }
  }
  const newState = { ...state }
  if (action.type === 'addressId') {
    if (!state.selectRegistered) {
      // 選択モード中でなければ無効
      return state
    }
    newState.addressId = action.value
    newState.inputs = mstAddressesMap.get(newState.addressId)
    return newState
  } else if (action.type === 'selectRegistered') {
    // 請求書送付先指定時は会社名を固定する
    newState.selectRegistered = action.value
    if (!sendMode && !readOnly) {
      newState.inputs.company_name = newState.selectRegistered
        ? (newState.addressId > -1 ? mstAddressesMap.get(newState.addressId).company_name : '')
        : companyName
    }
    return newState
  } else if (action.type === 'inputs') {
    for (const property in action.value) {
      newState.inputs[property] = action.value[property]
    }
    return newState
  } else {
    return defaultState
  }
}

export default function AddressSetter (props) {
  const sendMode = ['sending', 'sending-without-billing'].includes(props.mode)
  const mstAddressesMap = useRef(
    new Map(
      props.mstAddresses
        .filter((address) => sendMode ? true : !address.for_send)
        .map((address) => [address.id, address])
    )
  )
  const noOptions = mstAddressesMap.current.size === 0

  const [useSameAddress, setUseSameAddress] = useState(props.mode === 'sending' && props.useSameAddress)
  const [state, stateDispatcher] = useReducer(
    stateReducer(mstAddressesMap.current, props.readonly, sendMode, props.companyName),
    {},
    (args) => {
      // initialize state
      const oldInputs = props.oldInputs || {
        id: null,
        postal_code: '',
        address: '',
        company_name: '',
        department: ''
      }
      oldInputs.id = parseInt(oldInputs.id)
      if (props.readonly || noOptions) {
        oldInputs.select_registered = false
      } else if (!('select_registered' in oldInputs)) {
        oldInputs.select_registered = !noOptions
      }

      const defaultAddressId = mstAddressesMap.current.has(oldInputs.id)
        ? oldInputs.id
        : (oldInputs.select_registered ? mstAddressesMap.current.values().next().value.id : -1)
      const initialAddress = defaultAddressId > -1 ? mstAddressesMap.current.get(defaultAddressId) : null
      return {
        selectRegistered: oldInputs.select_registered,
        addressId: defaultAddressId,
        inputs: {
          id: oldInputs.id || (initialAddress?.id ?? ''),
          postal_code: oldInputs.postal_code || (initialAddress?.postal_code ?? ''),
          address: oldInputs.address || (initialAddress?.address ?? ''),
          company_name: oldInputs.company_name || (initialAddress?.company_name ?? props.companyName),
          department: oldInputs.department || (initialAddress?.department ?? '')
        }
      }
    }
  )

  function onUseSameAddressChanged (value) {
    const elem = document.getElementById('sending_licensee_name')
    if (elem) {
      if (value) {
        elem.classList.add('d-none')
      } else {
        elem.classList.remove('d-none')
      }
    }
  }

  function formatPostalCode () {
    // 郵便番号をxxx-xxxxの形に変換する
    let value = state.inputs.postal_code
      .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/[^0-9]/g, '')
    if (value.length > 7) {
      value = value.slice(0, 7)
    }
    if (value.length > 3) {
      value = value.slice(0, 3) + '-' + value.slice(3)
    }
    stateDispatcher({ type: 'inputs', value: { postal_code: value } })
  }

  onUseSameAddressChanged(useSameAddress)

  return (
    <>
      {sendMode && props.mode !== 'sending-without-billing' && (
        <mui.FormControlLabel
          control={
            <mui.Checkbox
              checked={useSameAddress}
              disabled={props.readonly || props.isShopro}
              onChange={(e) => {
                setUseSameAddress(e.target.checked)
              }}
            />
          }
          label="請求書送付先と同じ宛先を使用する"
        />
      )}
      {(!sendMode || !useSameAddress) && (
        <>
          <div className={props.readonly || props.isShopro ? 'd-none' : 'my-input-root'}>
            <mui.FormControl component="fieldset">
              <mui.RadioGroup aria-label="select-registered" value={state.selectRegistered} onChange={(e, value) => stateDispatcher({ type: 'selectRegistered', value: value === 'true' })}>
                <mui.FormControlLabel value={false} control={<mui.Radio />} label={`新たに${sendMode ? '証紙送付先' : '請求書送付先'}を登録する`} />
                <mui.FormControlLabel value={true} disabled={noOptions} control={<mui.Radio />} label={`登録済み${sendMode ? '証紙送付先' : '請求書送付先'}から選択する`} />
              </mui.RadioGroup>
            </mui.FormControl>
          </div>
          <div className={(!(props.readonly || props.isShopro) && state.selectRegistered) ? 'my-input-root' : 'd-none'}>
            {SelectRegistered(Array.from(mstAddressesMap.current.values()), state.addressId, (id) => stateDispatcher({ type: 'addressId', value: id }))}
          </div>
          <div className={(!(props.readonly || props.isShopro) && state.selectRegistered) ? 'd-none' : ''}>
            <div className="my-input-root w-50">
              <mui.TextField className='w-100' required={props.required} value={state.inputs.postal_code} label="郵便番号"
                variant="standard" type="string" placeholder=""
                error={props.errors.postal_code != null}
                helperText={props.errors.postal_code ? props.errors.postal_code[0] : ''}
                disabled={props.readonly || props.isShopro}
                InputProps={{
                  readOnly: props.readonly || props.isShopro
                }}
                onChange={(e) => {
                  stateDispatcher({ type: 'inputs', value: { postal_code: e.target.value } })
                }}
                onBlur={(e) => {
                  formatPostalCode()
                }}
              />
            </div>
            <div className="my-input-root w-100">
              <mui.TextField className='w-100' required={props.required} value={state.inputs.address} label="住所"
                variant="standard" type="string" placeholder=""
                error={props.errors.address != null}
                helperText={props.errors.address ? props.errors.address[0] : ''}
                disabled={props.readonly || props.isShopro}
                InputProps={{
                  readOnly: props.readonly || props.isShopro
                }}
                inputProps={{
                  maxLength: 100
                }}
                onChange={(e) => {
                  stateDispatcher({ type: 'inputs', value: { address: e.target.value } })
                }}
              />
            </div>
            <div className={`my-input-root w-100 ${(!sendMode && !props.readonly) ? 'my-input-disabled' : ''}`}>
              <mui.TextField className='w-100' required={props.required} value={state.inputs.company_name} label="会社名"
                variant="standard" type="string" placeholder=""
                error={props.errors.company_name != null}
                helperText={props.errors.company_name ? props.errors.company_name[0] : ''}
                disabled={!sendMode || props.readonly || props.isShopro}
                InputProps={{
                  readOnly: !sendMode || props.readonly || props.isShopro
                }}
                inputProps={{
                  maxLength: 100
                }}
                onChange={(e) => {
                  stateDispatcher({ type: 'inputs', value: { company_name: e.target.value } })
                }}
              />
            </div>
            {(!sendMode && !props.readonly) && (
              <div className="mb-2">※請求書送付先は契約当事者の会社名が記載されます。</div>
            )}
            {(sendMode && !props.readonly && state.inputs.company_name !== props.companyName) && (
              <BootstrapAlert class="danger">
                <div>証紙送付先に契約当事者と異なる会社名を指定しようとしています。よくご確認ください。</div>
              </BootstrapAlert>
            )}
            <div className="my-input-root w-100">
              <mui.TextField className='w-100' required={false} value={state.inputs.department} label="部署名"
                variant="standard" type="string" placeholder=""
                error={props.errors.department != null}
                helperText={props.errors.department ? props.errors.department[0] : ''}
                disabled={props.readonly || props.isShopro}
                InputProps={{
                  readOnly: props.readonly || props.isShopro
                }}
                inputProps={{
                  maxLength: 100
                }}
                onChange={(e) => {
                  stateDispatcher({ type: 'inputs', value: { department: e.target.value } })
                }}
              />
            </div>
          </div>
        </>
      ) }
      <input type="text" name={sendMode ? 'sending_address' : 'billing_address'}
        className='d-none'
        value={JSON.stringify({
          ...state.inputs,
          use_same_address: useSameAddress,
          select_registered: state.selectRegistered
        })}
        readOnly
      />
    </>
  )
}

AddressSetter.propTypes = {
  mstAddresses: PropTypes.arrayOf(PropTypes.object),
  companyName: PropTypes.string,
  oldInputs: PropTypes.object,
  errors: PropTypes.object,
  readonly: PropTypes.bool,
  isShopro: PropTypes.bool,
  useSameAddress: PropTypes.bool,
  mode: PropTypes.oneOf(['sending', 'sending-without-billing', 'billing']),
  required: PropTypes.bool
}

AddressSetter.defaultProps = {
  useSameAddress: true,
  mode: 'sending',
  required: true
}

$('.address-setter').each((i, elem) => {
  const qel = $(elem)
  const props = qel.data() // bladeでdata-xxxx形式で指定した値がpropsとして参照できる
  ReactDOM.render(<AddressSetter {...props}/>, elem)

  Object.keys(props).map((key) => { // ソースにdata-属性が残らないように削除
    qel.removeAttr('data-' + key.replace(/[A-Z]/g, (s) => '-' + s.charAt(0).toLowerCase()))
    return 0
  })
})
